import React, { useContext, useState, useEffect } from 'react';
import Api from 'api';
import { FormGrid, FormField, FormRow, FormColumn, FormSubmit } from 'components/forms';
import { Input } from 'components/UI';
import withForm from 'components/forms/withForm';
import { ErrorsShakeHubContext, findFirstFieldWithError } from 'components/forms/utils';
import { corporateFormSchema } from 'components/forms/validationSchemas';
import { CaptchaInfo } from '@landing/ui';
import { pushCustomSubmitEvent } from 'utils';
import { getCountryCode } from 'utils/localised';
import { phoneCode } from 'define/locale';

import * as styles from './CorporateForm.module.scss';

const Form = ({
  handleSubmit,
  fields,
  isValid,
  values,
  errors,
  isSubmitting,
  status,
  setStatus,
  formName,
  id,
  className,
  AdditionalInformationForForm,
}) => {
  const errorsShakeHub = useContext(ErrorsShakeHubContext);
  const [btnPressed, setBtnPressed] = useState(false);
  const captchaText = `This site is protected by reCAPTCHA, and the <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer noopener">Google Privacy Policy</a> and <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer noopener">Terms of Service apply</a>.`;

  useEffect(() => {
    if (!isValid && btnPressed) {
      errorsShakeHub.dispatchEvent(new CustomEvent('shakeError'));
      errorsShakeHub.dispatchEvent(
        new CustomEvent('setFocus', {
          detail: findFirstFieldWithError(values, errors),
        }),
      );
      setBtnPressed(false);
    }
  }, [isValid, btnPressed]);

  useEffect(() => {
    if (status === 'success') {
      setStatus();
    }
  }, [status]);

  return (
    <FormGrid
      submitHandler={e => {
        setBtnPressed(true);
        handleSubmit(e);
      }}
      formName={formName}
      id={id}
      className={className}
    >
      <FormRow sizes={['sm12']}>
        <FormColumn>
          <FormField Component={Input} {...fields.name} />
          <FormField Component={Input} {...fields.email} />
          <FormField Component={Input} {...fields.phone} />
          <FormField Component={Input} {...fields.message} />
          <FormField Component={Input} {...fields.linkedIn} />
        </FormColumn>
      </FormRow>
      {AdditionalInformationForForm}
      <div className={styles.submitWrap}>
        <FormSubmit
          title={'Register'}
          nda={fields.nda}
          photoConsent={fields.photoConsent}
          disabled={isSubmitting}
          size="sm"
        />
      </div>
      {captchaInfo && (
        <CaptchaInfo className={styles.captcha} theme="london" text={captchaText} />
      )}
    </FormGrid>
  );
};

export default function CorporateForm({
  id = 'dine-and-learn-malta',
  formName = 'dine-and-learn-malta',
  closeModal,
  onSuccess,
  additionalFormData,
  className,
  getAdditionalFormData,
  AdditionalInformationForForm = '',
  inModal = false,
  captchaInfo,
  withNda = true,
  withPhotoConsent = true,
}) {
  const fields = {
    name: {
      initialValue: '',
      domProps: {
        placeholder: 'Full name',
        maxLength: 255,
      },
    },
    email: {
      initialValue: '',
      inModal,
      domProps: {
        placeholder: 'Corporate email',
        type: 'email',
        maxLength: 254,
      },
    },
    phone: {
      initialValue: '',
      getInitialValue,
      mask: {
        mask: '+99999999999999999',
        maskChar: '',
        formatChars: {
          9: '[0-9]',
        },
      },
      domProps: {
        placeholder: 'Phone number',
        type: 'tel',
      },
    },
    message: {
      initialValue: '',
      isTextarea: true,
      domProps: {
        maxLength: 500,
        placeholder: 'Position in your company',
      },
    },
    linkedIn: {
      initialValue: '',
      domProps: {
        maxLength: 256,
        placeholder: 'LinkedIn Profile ',
      },
    },
  };

  if (withNda) {
    fields.nda = {
      initialValue: false,
      inModal,
      withoutError: true,
      content: 'I want to keep my personal data private',
    };
  }

  if (withPhotoConsent) {
    fields.photoConsent = {
      initialValue: false,
      inModal,
      withoutError: true,
      content: 'I consent to having my photograph taken and used in promotional materials',
    };
  }

  const errorsMessages = {
    name: 'Please enter your full name',
    email: {
      required: 'Please enter your corporate email',
      notCorporateEmail: 'Please enter your corporate email',
    },
    phone: 'Please enter your phone number',
    message: 'Please enter your position',
    linkedIn: 'Please enter a valid LinkedIn URL',
  };

  const Component = withForm({
    fields,
    validation: props => corporateFormSchema(errorsMessages, props),
    submit: {
      id,
      formName,
      handler: Api.sendForm.bind(Api, 'integration/form-data'),
      reset: true,
      successModal: 'SuccessModal',
      errorModal: 'ErrorModal',
      onSuccess,
      additionalFormData,
      getAdditionalFormData,
      pushSubmitEvent: () => pushCustomSubmitEvent(id),
      onResponse: () => {
        if (inModal) {
          closeModal();
        }
      },
    },
    formName,
    id,
    className,
    AdditionalInformationForForm,
  })(Form);

  return <Component />;
}

function getInitialValue() {
  if (typeof window !== 'undefined') {
    const country = getCountryCode()?.toLowerCase();
    return phoneCode[country] ?? '+';
  }
  return '+';
}
